<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CrvSearchInput from '@v3c/components/CrvSearchInput.vue'
// import ConnectionStatus from '@v3c/components/ConnectionStatus.vue'
import highlight from 'vue-highlight-words'
import { useDisplay } from 'vuetify'
import { mdiChevronRight } from '@mdi/js'
import type { InternalItem } from 'vuetify/lib/types.mjs'

const { thresholds, xs, mdAndUp } = useDisplay()

// TODO: in order to get this page to work, the consumer app needs to import the router... or Do we really?


export interface CustomerProjection {
  id: string | number
  name: string
  number: string | number
  ubn?: string
  locations?: []
}


// * interface: (props and emits)
const props = withDefaults(
  defineProps<{
    customers: CustomerProjection[]
    fetchCustomers?: (search: string) => Promise<CustomerProjection[]> 
    customerSelectTitle?: string
    customerNameLabel?: string
    customerNumberLabel?: string
    customerSearchPlaceholder?: string
    customerNoDataLabel?: string,
    customersPerPage?: number,
    minimumSearchLength?: number,
    showCustomersPagination?: boolean,
    customerSearchHint?: string,
  }>(),
  {
    customers: () => [],
    fetchCustomers: undefined,
    customerSelectTitle: "$t('appshell.selectCustomerTitle')",
    customerNameLabel: "$t('appshell.customerNameLabel')",
    customerNumberLabel: "$t('appshell.customerNumberLabel')",
    customerSearchPlaceholder: "$t('appshell.customerSearchPlaceholder')",
    customerNoDataLabel: "$t('appshell.customerNoDataLabel')",
    customersPerPage: 10,
    minimumSearchLength: 0,
    showCustomersPagination: true,
    customerSearchHint: '',
  }
)

const emit = defineEmits(['customer-selected', 'canceled', 'create-customer'])


// * refs
const searchValue = ref('')
const fetchedCustomers = ref<CustomerProjection[]>([])
const loading = ref(false)
const itemsPerPage = ref(props.customersPerPage ?? -1)
const sortBy = ref([
    {
        key: 'name',
        order: 'asc' as 'asc' | 'desc'
    },
])


// * computeds
const cols = computed(() => xs.value ? '12' : undefined)
const elevation = computed(() => mdAndUp.value ? 1 : 0)
const classes = computed(() => mdAndUp.value ? 'mx-auto pt-4' : 'mx-sm-n4')

const headers = computed(() => {
    const number = {
        key: 'number',
        title: props.customerNumberLabel,
        headerProps: { class: 'text-left font-weight-medium' },
        cellProps: { class: 'text-left' },
    }
    const name =  {
        key: 'name',
        title: props.customerNameLabel,
        sorted: true,
        headerProps: { class: 'text-left' },
        cellProps: { class: 'text-left' },
    }
    const actions = {
        key: 'actions',
        sortable: false,
        headerProps: { class: 'text-end' },
        cellProps: { class: 'text-end' },
    }
    if (xs.value) {
        return [
            number,
            name
        ]
    }
    return [
        number,
        name,
        actions
    ]
})

const items = computed(() => {

  // TODO: If customers for that particular user are not that many, we could fetch them all at once and filter locally ???

  // If both props are provided, prefer fetchCustomers
  if (props.fetchCustomers) {
    return fetchedCustomers.value
  }

  // fallback: filter locally static customers
  if (searchValue.value.length < props.minimumSearchLength) {
    return []
  }
  const term = searchValue.value.toLowerCase()
  return props.customers.filter(customer =>
    String(customer.number).toLowerCase().includes(term) ||
    customer.name.toLowerCase().includes(term) ||
    (customer.ubn && customer.ubn.toLowerCase().includes(term))
  )
})


watch(searchValue, async (newVal) => {
  if (!props.fetchCustomers) return
  if (newVal.length < props.minimumSearchLength) {
    fetchedCustomers.value = []
    return
  }

  loading.value = true
  try {
    fetchedCustomers.value = await props.fetchCustomers(newVal)
  } finally {
    loading.value = false
  }
})


// * methods

const handleClickCustomer = (_evt: PointerEvent, payload: { item: CustomerProjection }) => {
  const customer: CustomerProjection = {
    id: payload.item.id,
    name: payload.item.name,
    number: payload.item.number,
    ubn: payload.item.ubn,
    locations: payload.item.locations ?? [],
  }
  emit('customer-selected', customer)
}

const searchFilter = (_value: string, query: string, item?: InternalItem<CustomerProjection>): boolean => {
  if (!item) {
    return false
  }

  const q = query.toLowerCase()
  const customer = item.raw
  return customer.name.toLowerCase().includes(q) || customer.number.toString().toLowerCase().includes(q) || (!!customer.ubn && customer.ubn.includes(q))
}
</script>


<template>
  <v-card
    :class="classes"
    :max-width="thresholds.md"
    :elevation="elevation"
    class="mt-4"
  >
    <v-card-title class="py-0">
      <h2 class="d-none d-sm-flex">
        {{ props.customerSelectTitle }}
        <!-- <connection-status class="ml-2" />  -->
      </h2>
    </v-card-title>
    <v-card-text>
      <v-container
        class="mx-0 px-0 pt-4 pb-4"
      >
        <v-row>
          <v-col
            class="pt-0"
            :cols="cols"
          >
            <crv-search-input
              v-model="searchValue"
              :placeholder="customerSearchPlaceholder"
              :error="false"
              :hide-details="false"
              :hint="customerSearchHint"
              :persistent-hint="!!customerSearchHint"
            />
          </v-col>
        </v-row>
      </v-container>
      <v-sheet class="mx-n4 mx-sm-0">
        <v-data-table
          v-model:items-per-page="itemsPerPage"
          v-model:sort-by="sortBy"
          density="compact"
          :no-data-text="customerNoDataLabel"
          :headers="headers"
          :items="items"
          :loading="loading"
          :search="searchValue"
          :custom-filter="searchFilter"
          :hover="!!items.length"
          @click:row="handleClickCustomer"
        >
          <template #[`item.number`]="{ item }">
            <highlight
              :search-words="[searchValue]"
              :text-to-highlight="String(item.number ?? '')" 
            />
          </template>
          <template #[`item.name`]="{ item }">
            <div>
              <highlight
                :search-words="[searchValue]"
                :text-to-highlight="item.name || ''"
              />
              <highlight
                :search-words="[searchValue]"
                :text-to-highlight="item.ubn || ''"
                class="text-grey-darken-1 d-block"
              />
            </div>
          </template>
          <template #[`item.actions`]="{}">
            <v-btn
              size="small"
              :icon="mdiChevronRight"
              :ripple="false"
              variant="plain"
            />
          </template>
          <template
            v-if="!showCustomersPagination"
            #bottom
          />
        </v-data-table>
      </v-sheet>
    </v-card-text>
  </v-card>
</template>


<style lang="scss" scoped>
@use '@v3c/assets/styles/variables.scss' as *;

:deep(.name-xs) {
    padding-left: 0 !important;
}

</style>